import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Pencil, Trash2 } from 'lucide-react';
import { expenseCaptureApi } from '../../services/expenseCaptureApi';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../ui/ConfirmModal';
import type { ExpenseCategory, ExpenseReceipt, ExpenseReceiptFile } from '../../types/expense';
import { StatusChip } from './StatusChip';
import { ReceiptImage } from './ReceiptImage';
import { formatMoney, formatDate } from './format';

interface Props {
    receiptId: number;
    currentUserEmail: string;
    categories: ExpenseCategory[];
    onBack: () => void;
    onResume: (id: number) => void;
    onChanged: () => void;
}

export function ExpenseDetail({ receiptId, currentUserEmail, categories, onBack, onResume, onChanged }: Props) {
    const { toast } = useToast();
    const [receipt, setReceipt] = useState<ExpenseReceipt | null>(null);
    const [files, setFiles] = useState<ExpenseReceiptFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        expenseCaptureApi.getReceipt(receiptId)
            .then(({ receipt, files }) => { if (active) { setReceipt(receipt); setFiles(files); } })
            .catch(() => { if (active) { toast('Could not load this receipt.', 'error'); onBack(); } })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [receiptId, toast, onBack]);

    const handleDelete = async () => {
        try {
            await expenseCaptureApi.deleteReceipt(receiptId);
            toast('Draft deleted.', 'success');
            onChanged();
            onBack();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Could not delete.', 'error');
        }
    };

    if (loading || !receipt) {
        return (
            <div className="flex items-center justify-center gap-2 py-16 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading…
            </div>
        );
    }

    const isOwn = receipt.UserEmail.toLowerCase() === currentUserEmail.toLowerCase();
    const isDraft = receipt.Status === 'Draft';
    const category = categories.find(c => c.id === receipt.CategoryId) ?? null;
    let declaration: Record<string, string> = {};
    try { declaration = receipt.DeclarationJson ? JSON.parse(receipt.DeclarationJson) : {}; } catch { declaration = {}; }

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <button type="button" onClick={onBack} className="-ml-2 rounded-lg p-2 text-gray-500 hover:bg-gray-100">
                    <ArrowLeft className="h-5 w-5" />
                </button>
                <h2 className="flex-1 truncate text-lg font-semibold text-gray-900">
                    {receipt.Vendor || receipt.CategoryLabel || 'Receipt'}
                </h2>
                <StatusChip status={receipt.Status} />
            </div>

            <div className="space-y-2">
                {files.map(f => (
                    <ReceiptImage key={f.Id} receiptId={receipt.Id} page={f.PageNo}
                        className="h-72 w-full rounded-xl border border-gray-200" />
                ))}
                {files.length === 0 && (
                    <ReceiptImage receiptId={receipt.Id} className="h-72 w-full rounded-xl border border-gray-200" />
                )}
            </div>

            <dl className="divide-y divide-gray-100 rounded-xl border border-gray-200">
                <Row label="Amount" value={formatMoney(receipt.GrossAmount, receipt.Currency)} strong />
                <Row label="Date" value={formatDate(receipt.ExpenseDate)} />
                <Row label="Category" value={receipt.CategoryLabel || '—'} />
                <Row label="Vendor" value={receipt.Vendor || '—'} />
                <Row label="VAT" value={receipt.VatAmount != null ? formatMoney(receipt.VatAmount, receipt.Currency) : '—'} />
                <Row label="Country / company" value={`${receipt.CountryCode || '—'} · ${receipt.CompanyCode.toUpperCase()}`} />
                {(category?.fields ?? []).map(fld => (
                    <Row key={fld.key} label={fld.label} value={declaration[fld.key] || '—'} />
                ))}
                {receipt.Description && <Row label="Note" value={receipt.Description} />}
                {!isOwn && <Row label="Captured by" value={receipt.UserEmail} />}
            </dl>

            {isOwn && isDraft && (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setConfirmDelete(true)}
                        className="flex items-center gap-1.5 rounded-xl border border-gray-300 px-3 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                    >
                        <Trash2 className="h-4 w-4" /> Delete
                    </button>
                    <button
                        type="button"
                        onClick={() => onResume(receipt.Id)}
                        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 py-2.5 font-medium text-white hover:bg-blue-700"
                    >
                        <Pencil className="h-4 w-4" /> Continue editing
                    </button>
                </div>
            )}

            {confirmDelete && (
                <ConfirmModal
                    title="Delete draft?"
                    message="This removes the draft and its photo. This cannot be undone."
                    confirmLabel="Delete"
                    onConfirm={() => { setConfirmDelete(false); void handleDelete(); }}
                    onCancel={() => setConfirmDelete(false)}
                />
            )}
        </div>
    );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
    return (
        <div className="flex items-center justify-between gap-3 px-3 py-2.5">
            <dt className="text-sm text-gray-500">{label}</dt>
            <dd className={`text-right text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>{value}</dd>
        </div>
    );
}
